import { call, delay, put, select } from "redux-saga/effects";

import { _sharedWorkerFetchToc } from "../toc/saga";
import { getBookIdNumber, getBookIdWithPrefix } from "../../utils/content/content";
import { getReaderAudioChapter } from "../../utils/reader/reader";
import {
  findWaveformsDictionaryParaById,
  findWaveformsDictionaryParaByPosition
} from "../../utils/player";
import { selectorPlayerWaveformsDictionary } from "../player/selectors";
import { fetchAndStoreWaveformsDictionary, getBookById } from "../saga-functions";
import { makeWaveformsJsonUrl } from "../../utils/url/url";
import { selectorHistoryAllHistory } from "./selectors";
import { detectIfLastRead, HistoryType } from "../../../main/src/utils/Utils";
import { isProduction } from "../../utils/assembly";
import { selectorSystemIsLogin, selectorSystemOnLine, selectorUserInfo } from "../system/selectors";
import {
  joinHistoryData,
  mapListenHistoryForServer,
  mapReadingHistoryForServer
} from "../../api/not-sorted.utils";
import {
  addBooksToLibraryRequest, deleteBooksFromLibraryRequest,
  getAllHistoryRequest,
  putHistoryRequest, setLibraryLanguagesRequest
} from "../../api/not-sorted";
import { consoleLogError } from "../../utils/console";
import {
  postSWMessageCacheOfflineHistoryListen,
  postSWMessageCacheOfflineHistoryRead
} from "../../utils/sw/sw";
import { _sharedActionsLibrary } from "./actions";
import { _sharedActionsSystem } from "../system/actions";
import { LOADER_ID_FETCH_ALL_HISTORY } from "./constants";
import { _sharedWorkerFetchBooksByIds } from "../mainTree/saga";
import { selectorMainTreeBooks } from "../mainTree/selectors";
import { selectorSetting } from "../settings/selectors";
import { SETTING } from "../../utils/settings/constants";
import { _sharedActionsSettings } from "@/shared/store/settings/actions";
import { isBeforeDateISO } from "@/shared/utils/date";

function* _getWaveformsDictionary(audioChapter) {
  const audioChapterId = audioChapter.id;

  const waveformsDictionary = yield select(selectorPlayerWaveformsDictionary);

  if (!waveformsDictionary[audioChapterId]) {
    yield fetchAndStoreWaveformsDictionary(makeWaveformsJsonUrl(audioChapter.mp3), audioChapterId);
    return yield select(selectorPlayerWaveformsDictionary);
  }

  return waveformsDictionary;
}

// Detect exactly {paraId} based on listen {chapterId} and {position}.
// We can't store {paraId} for LISTEN history. (we can do it for ML though)
function* _getParaIdByChapterIdAndPosition(chapterId, position) {
  const toc = yield _sharedWorkerFetchToc({ data: getBookIdNumber(chapterId) });

  if (toc?.length) {
    const audioChapter = getReaderAudioChapter(toc, chapterId);
    if (audioChapter) {
      const audioChapterId = audioChapter.id;

      const waveformsDictionary = yield _getWaveformsDictionary(audioChapter);
      const foundParaId = findWaveformsDictionaryParaByPosition(
        waveformsDictionary, audioChapterId, position
      )?.id;

      if (foundParaId) {
        return foundParaId;
      }
    }
  }

  return chapterId;
}

// Detect {audioChapterId} and {position} based on {chapterId} and {paraId}.
function* _getAudioParamsByChapterAndParaId(chapterId, paraId) {
  const toc = yield _sharedWorkerFetchToc({ data: getBookIdNumber(chapterId) });

  let audioChapterId = chapterId, position = 0;
  if (toc?.length) {
    const audioChapter = getReaderAudioChapter(toc, chapterId);
    if (audioChapter) {
      audioChapterId = audioChapter.id;

      const waveformsDictionary = yield _getWaveformsDictionary(audioChapter);
      const foundPosition = findWaveformsDictionaryParaById(
        waveformsDictionary, audioChapterId, paraId
      )?.start;

      if (foundPosition !== undefined) {
        position = parseInt(foundPosition);
      }
    }
  }

  return {
    id: audioChapterId,
    position
  };
}

export function* _sharedWorkerGetIdAndPositionForBookOpen(
  bookId,
  isGonnaListen,
  isUseMLHistory, isUseDefaultHistory, isUseLocalHistory,
  isFetchNewestHistory,
) {
  let id, chapterId, paraId, position, inLibrary,
    isNeedToDetectParaId, isNeedToDetectAudioChapterParams;

  const isUseHistory = isUseMLHistory || isUseDefaultHistory || isUseLocalHistory;

  if (isUseHistory) {
    if (isFetchNewestHistory) {
      // TODO: Add {start} param
      yield call(_sharedWorkerFetchAllHistory);
    }

    const allHistory = yield select(selectorHistoryAllHistory);
    const bookHistory = allHistory.find((item) => item.id === bookId);

    if (bookHistory) {
      const isHistoryMyLibraryNewerThanLocal =
        isGonnaListen
          ? isBeforeDateISO(bookHistory.luListenLocal, bookHistory.mlLu)
          : isBeforeDateISO(bookHistory.luReadLocal, bookHistory.mlLu);
      const isHistoryMyLibraryNewerThanDefault = isGonnaListen
        ? isBeforeDateISO(bookHistory.luListen, bookHistory.mlLu)
        : isBeforeDateISO(bookHistory.luRead, bookHistory.mlLu);
      const isHistoryDefaultNewerThanLocal = isGonnaListen
        ? isBeforeDateISO(bookHistory.luListenLocal, bookHistory.luListen)
        : isBeforeDateISO(bookHistory.luReadLocal, bookHistory.luRead);

      const isNewestHistoryML = isHistoryMyLibraryNewerThanDefault
        && isHistoryMyLibraryNewerThanLocal;
      const isNewestHistoryDefault = !isHistoryMyLibraryNewerThanLocal
        && isHistoryDefaultNewerThanLocal;

      inLibrary = !!bookHistory.inLibrary;

      const isMLHistorySuitable = isNewestHistoryML && isUseMLHistory && inLibrary;
      const isDefaultHistorySuitable = (isNewestHistoryDefault || !isUseLocalHistory)
        && isUseDefaultHistory;
      const isLocalHistorySuitable = isUseLocalHistory;

      const timestampRead = isMLHistorySuitable
        ? bookHistory.luRead // use default history timestamp, because ML has only one {mlLu}
        : isDefaultHistorySuitable
          ? bookHistory.luRead
          : isLocalHistorySuitable
            ? bookHistory.luReadLocal
            : undefined;

      const timestampListen = isMLHistorySuitable
        ? bookHistory.luListen // use default history timestamp, because ML has only one {mlLu}
        : isDefaultHistorySuitable
          ? bookHistory.luListen
          : isLocalHistorySuitable
            ? bookHistory.luListenLocal
            : undefined;

      // Default history is saved every time.
      const isLastRead = detectIfLastRead(timestampRead, timestampListen);

      if (isLastRead && isGonnaListen) {
        isNeedToDetectAudioChapterParams = true;
      }

      if (!isLastRead && !isGonnaListen) {
        isNeedToDetectParaId = true;
      }

      if (isMLHistorySuitable) {
        position = bookHistory.mlPosition;
        chapterId = bookHistory.mlChapterId;

        paraId = bookHistory.mlParaId;

        id = isGonnaListen ? bookHistory.mlChapterId : bookHistory.mlParaId;
      } else if (isDefaultHistorySuitable) {
        position = isLastRead ? bookHistory.readPosition : bookHistory.listenPosition;
        chapterId = isLastRead ? bookHistory.readChapterId : bookHistory.listenChapterId;

        paraId = bookHistory.readParaId;

        if (isLastRead) {
          id = isGonnaListen ? bookHistory.readChapterId : bookHistory.readParaId;
        } else {
          id = bookHistory.listenChapterId;
        }
      } else if (isLocalHistorySuitable) {
        chapterId = isLastRead ? bookHistory.readChapterIdLocal : bookHistory.listenChapterIdLocal;
        position = isLastRead ? bookHistory.readPositionLocal : bookHistory.listenPositionLocal;

        paraId = bookHistory.readParaIdLocal;

        if (isLastRead) {
          id = isGonnaListen ? bookHistory.readChapterIdLocal : bookHistory.readParaIdLocal;
        } else {
          id = bookHistory.listenChapterIdLocal;
        }
      }
    }
  }

  if (isNeedToDetectParaId && chapterId) {
    id = yield _getParaIdByChapterIdAndPosition(chapterId, position);
  }

  if (isNeedToDetectAudioChapterParams && chapterId) {
    const audioData = yield _getAudioParamsByChapterAndParaId(chapterId, paraId);
    id = audioData.id;
    position = audioData.position;
  }

  if (!id) {
    const book = yield getBookById(bookId, false);

    if (book) {
      id = book.first_para;
    } else {
      const value = getBookIdNumber(bookId);
      id = value + ".1";
    }

    position = 0;
  }

  return {
    id,
    position,
    inLibrary,
  };
}

const SYNC_HISTORY_TYPE_DEBOUNCE_MS = 3000;
const IS_CONSOLE_LOGS = true;

const TAG = "Saga Library";

function* _saveHistoryMethod(
  historyType, chapterId, paraId, position, saveML, localItemDateISO, skipSaveOnServer
) {
  const bookId = getBookIdWithPrefix(paraId);
  const isReadHistory = historyType === HistoryType.READING;
  const isListenHistory = historyType === HistoryType.LISTEN;

  const userInfo = yield select(selectorUserInfo);
  const allHistory = yield select(selectorHistoryAllHistory);
  const {
    isLogin,
    onLine
  } = yield select((s) => ({
    isLogin: selectorSystemIsLogin(s),
    onLine: selectorSystemOnLine(s),
  }));
  const shouldSaveOnServer = isLogin && onLine && !skipSaveOnServer;

  const item = allHistory.find((item) => item.id === bookId);
  // for next app it's enough just to have a book in the library.
  const isInLibrary = item?.inLibrary;

  const isSyncMLAlso = isInLibrary;

  const date = new Date();
  const currentDateISO = date.toISOString();
  const historyItem = { id: bookId };

  if (isReadHistory) {
    historyItem.readChapterIdLocal = chapterId;
    historyItem.readParaIdLocal = paraId;
    historyItem.readPositionLocal = position;
    historyItem.luReadLocal = localItemDateISO || currentDateISO;
    if (IS_CONSOLE_LOGS) {
      console.log("%c[History] SAVE Read", "background: red; color: white;", {
        shouldSaveOnServer,
        chapterId,
        paraId,
        position: historyItem.readPosition,
      });
    }
  } else {
    historyItem.listenChapterIdLocal = chapterId;
    historyItem.listenPositionLocal = position;
    historyItem.luListenLocal = localItemDateISO || currentDateISO;
    if (IS_CONSOLE_LOGS) {
      console.log("%c[History] SAVE Listen", "background: blue; color: white;", {
        shouldSaveOnServer,
        chapterId,
        paraId,
        position,
      });
    }
  }

  if (shouldSaveOnServer) {
    if (isReadHistory) {
      historyItem.luRead = currentDateISO;
      historyItem.readChapterId = chapterId;
      historyItem.readParaId = paraId;
      historyItem.readPosition = position;
    } else if (isListenHistory) {
      historyItem.luListen = currentDateISO;
      historyItem.listenChapterId = chapterId;
      historyItem.listenPosition = position;
    }

    if (isSyncMLAlso) {
      historyItem.mlLu = currentDateISO;
      historyItem.mlChapterId = chapterId;
      historyItem.mlParaId = paraId;
      historyItem.mlPosition = position;
      if (IS_CONSOLE_LOGS) {
        console.log("%c[ML] SAVE", "background: green; color: white;", {
          chapterId: historyItem.mlChapterId,
          paraId: historyItem.mlParaId,
          position: historyItem.mlPosition,
        });
      }
    }
  }

  const itemIndexInAllHistory = allHistory.findIndex((item) => item.id === bookId);

  if (itemIndexInAllHistory !== -1) {
    const item = allHistory[itemIndexInAllHistory];

    allHistory[itemIndexInAllHistory] = {
      ...(item || {}),
      ...historyItem,
    };
  } else {
    allHistory.push(historyItem);
  }

  yield put(_sharedActionsLibrary.updateAllHistory(userInfo?.id, allHistory));

  if (shouldSaveOnServer) {
    let itemToSave = {};
    if (isReadHistory) {
      itemToSave = mapReadingHistoryForServer(
        historyItem.readParaId,
        historyItem.readPosition,
        historyItem.luRead,
      );
    } else if (isListenHistory) {
      itemToSave = mapListenHistoryForServer(
        historyItem.listenChapterId,
        historyItem.listenPosition,
        historyItem.luListen,
      );
    }

    try {
      yield call(putHistoryRequest, historyType, {
        items: [itemToSave],
      });
    } catch (e) {
      consoleLogError(TAG, "Error put history", {
        e,
        historyType,
        args: { items: [itemToSave] },
        fn: putHistoryRequest,
      });
    }

    if (isSyncMLAlso) {
      try {
        yield call(putHistoryRequest, HistoryType.ML_HISTORY, {
          items: [
            {
              lu: historyItem.mlLu,
              chapter_id: historyItem.mlChapterId,
              para_id: historyItem.mlParaId,
              position: historyItem.mlPosition,
            },
          ],
        });
      } catch (e) {
        consoleLogError(TAG, "Error put ML history", {
          e,
          historyType: HistoryType.ML_HISTORY,
          args: {
            items: [
              {
                lu: historyItem.mlLu,
                chapter_id: historyItem.mlChapterId,
                para_id: historyItem.mlParaId,
                position: historyItem.mlPosition,
              },
            ],
          },
          fn: putHistoryRequest,
        });
      }
    }
  }

  if (item && (
    localItemDateISO !== (isReadHistory ? item.luReadLocal : item.luListenLocal)
  )) {
    if (isReadHistory) {
      postSWMessageCacheOfflineHistoryRead(chapterId, paraId, saveML, historyItem.luReadLocal);
    } else {
      postSWMessageCacheOfflineHistoryListen(chapterId, position, saveML, historyItem.luListenLocal);
    }
  }

  // fetch newly added or updated items as server computes some data on its side,
  // (e.g. chapter id for para id and ref codes)
  const dateToFetchNewItems = new Date(date.getTime() - 1000);
  yield put(_sharedActionsLibrary.fetchAllBooksData(dateToFetchNewItems.toISOString()));
}

export function* _sharedWorkerSaveAllHistory(action) {
  const {
    chapterId,
    paraId,
    position: positionNotParsed,
    saveML,
    delay: delayMS,
    localItemDateISO,
    skipSaveOnServer
  } = action.data;
  yield delay(delayMS ?? SYNC_HISTORY_TYPE_DEBOUNCE_MS);

  const position = positionNotParsed ? Number.parseInt(positionNotParsed, 10) : 0;
  yield _saveHistoryMethod(
    HistoryType.LISTEN, chapterId, paraId || chapterId, position, false,
    localItemDateISO, skipSaveOnServer
  );

  yield _saveHistoryMethod(
    HistoryType.READING, chapterId, paraId, position, saveML,
    localItemDateISO, skipSaveOnServer
  );
}

export function* _sharedWorkerSaveReadHistory(action) {
  const {
    chapterId,
    paraId,
    saveML,
    delay: delayMS,
    localItemDateISO,
    skipSaveOnServer
  } = action.data;
  yield delay(delayMS ?? SYNC_HISTORY_TYPE_DEBOUNCE_MS);

  yield _saveHistoryMethod(
    HistoryType.READING, chapterId, paraId, undefined, saveML,
    localItemDateISO, skipSaveOnServer
  );
}

export function* _sharedWorkerSaveListenHistory(action) {
  const {
    chapterId,
    position: positionNotParsed,
    saveML,
    delay: delayMS,
    localItemDateISO,
    skipSaveOnServer,
  } = action.data;
  const position = positionNotParsed ? Number.parseInt(positionNotParsed, 10) : 0;
  yield delay(delayMS ?? SYNC_HISTORY_TYPE_DEBOUNCE_MS);
  yield _saveHistoryMethod(
    HistoryType.LISTEN, chapterId, chapterId, position, saveML,
    localItemDateISO, skipSaveOnServer
  );
}

export function* _sharedHandleNewAllHistoryItems(allHistoryItems) {
  const userInfo = yield select(selectorUserInfo);

  const langLessItemsIndexes = [];

  let allHistoryItem;
  for (let i = 0; i < allHistoryItems.length; i++) {
    allHistoryItem = allHistoryItems[i];
    if (!allHistoryItem.lang) {
      langLessItemsIndexes.push(i);
    }
  }

  const langLessItemsIds = langLessItemsIndexes.map((i) => allHistoryItems[i].id);

  if (langLessItemsIds.length) {
    const books = yield call(_sharedWorkerFetchBooksByIds, langLessItemsIds);

    if (books) {
      let index, book;
      for (let i = 0; i < langLessItemsIndexes.length; i++) {
        index = langLessItemsIndexes[i];
        book = books.find((b) => b.id === langLessItemsIds[i]);
        if (book) {
          allHistoryItems[index] = {
            ...allHistoryItems[index],
            lang: book.lang,
          };
        }
      }
    }
  }

  yield put(_sharedActionsLibrary.updateAllHistory(userInfo?.id, allHistoryItems));
}

export function* _sharedWorkerFetchAllHistory(action) {
  const isLogin = yield select(selectorSystemIsLogin);

  if (!isLogin) {
    return;
  }

  let start, onFinally;

  if (action?.data) {
    start = action.data.start;
    onFinally = action.data.onFinally;
  }

  try {
    yield put(_sharedActionsSystem.loading(LOADER_ID_FETCH_ALL_HISTORY));
    const {
      reading,
      listen,
      ml,
      library
    } = yield call(getAllHistoryRequest, start);

    const allHistory = yield select(selectorHistoryAllHistory);
    const allHistoryItems = joinHistoryData(reading, listen, ml, library, allHistory, !!start);

    yield _sharedHandleNewAllHistoryItems(allHistoryItems);
  } catch (error) {
    console.log("fetchAllBooksDataWorker Error", error);
  } finally {
    yield put(_sharedActionsSystem.loading(LOADER_ID_FETCH_ALL_HISTORY, true));

    if (onFinally) {
      onFinally();
    }
  }
}

export function* _sharedWorkerToggleBookInMLById(action) {
  const ids = Array.isArray(action.data) ? action.data.map(getBookIdNumber) : [getBookIdNumber(action.data)];

  const userInfo = yield select(selectorUserInfo);
  const allHistory = yield select(selectorHistoryAllHistory);
  const books = yield select(selectorMainTreeBooks);
  const libraryLanguages = yield select(selectorSetting(SETTING.libraryLanguages.id));

  const allHistorySpread = [...allHistory];

  let suitableBooks = books.filter((book) => ids.includes(getBookIdNumber(book.id)));

  if (!suitableBooks.length) {
    suitableBooks = [];

    for (const id of ids) {
      const book = yield getBookById(id, false);
      suitableBooks.push(book);
    }
  }

  if (!suitableBooks) {
    consoleLogError("ML", "Couldn't load a book, so won't toggled in the ML!", {
      ids,
    });
    return;
  }

  const idsAdded = [];
  const idsRemoved = [];

  for (const book of suitableBooks) {
    const id = getBookIdNumber(book.id);
    const bookLang = book.lang;
    const bookIndex = allHistorySpread.findIndex((item) => getBookIdNumber(item.id) === id);

    let isAdded;

    if (bookIndex === -1) {
      allHistorySpread.push({
        id: getBookIdWithPrefix(id),
        lang: bookLang,
        inLibrary: true,
        luLibrary: new Date().toISOString(),
      });
      isAdded = true;
    } else {
      const inLibraryNew = !allHistorySpread[bookIndex].inLibrary;
      allHistorySpread[bookIndex].inLibrary = inLibraryNew;
      allHistorySpread[bookIndex].luLibrary = new Date().toISOString();
      isAdded = inLibraryNew;
    }

    if (isAdded) {
      idsAdded.push(id);
      if (bookLang && !libraryLanguages.includes(bookLang)) {
        const newLangs = [...libraryLanguages, bookLang];
        yield put(_sharedActionsLibrary.setLibraryLanguages(newLangs));
      }
    } else {
      idsRemoved.push(id);
    }
  }

  yield put(_sharedActionsLibrary.updateAllHistory(userInfo?.id, allHistorySpread));

  if (idsAdded.length) {
    yield call(addBooksToLibraryRequest, idsAdded);
    yield put(_sharedActionsSystem.toast("@toastAddToLibrary"));
  }

  if (idsRemoved.length) {
    yield call(deleteBooksFromLibraryRequest, idsRemoved);
    yield put(_sharedActionsSystem.toast("@toastRemoveFromLibrary"));
  }
}

export function* _sharedWorkerSetLibraryLanguages(action) {
  const langs = action.data;
  const isLogin = yield select(selectorSystemIsLogin);
  try {
    if (isLogin) {
      yield call(setLibraryLanguagesRequest, langs);
    }

    yield put(
      _sharedActionsSettings.updateSettings({
        [SETTING.libraryLanguages.id]: langs,
        [SETTING.filterLibraryLanguages.id]: langs,
      }),
    );
  } catch (error) {
    console.log("setLibraryLanguagesWorker Error", error);
  }
}
